import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IErpaxa, defaultValue } from 'app/shared/model/erpaxa.model';

const initialState: EntityState<IErpaxa> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/erpaxas';
const apiSearchUrl = 'api/_search/erpaxas';

// Actions

export const searchEntities = createAsyncThunk('erpaxa/search_entity', async ({ query, page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiSearchUrl}?query=${query}${sort ? `&page=${page}&size=${size}&sort=${sort}` : ''}`;
  return axios.get<IErpaxa[]>(requestUrl);
});

export const getEntities = createAsyncThunk('erpaxa/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IErpaxa[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'erpaxa/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IErpaxa>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'erpaxa/create_entity',
  async (entity: IErpaxa, thunkAPI) => {
    const result = await axios.post<IErpaxa>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'erpaxa/update_entity',
  async (entity: IErpaxa, thunkAPI) => {
    const result = await axios.put<IErpaxa>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'erpaxa/partial_update_entity',
  async (entity: IErpaxa, thunkAPI) => {
    const result = await axios.patch<IErpaxa>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'erpaxa/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IErpaxa>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const uploadDocument = createAsyncThunk(
  'erpaxa/upload',
  async ({ type, data }: any, thunkAPI) => {
    const result = await axios.post(`${apiUrl}/upload/${type}`, data);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const downloadDocument = createAsyncThunk(
  'erpaxa/download',
  async (type: string) => {
    return await axios.get(`${apiUrl}/download/${type}`);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const ErpaxaSlice = createEntitySlice({
  name: 'erpaxa',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, searchEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(downloadDocument), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = false;
      })
      .addMatcher(isFulfilled(uploadDocument), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = action.payload.status === 200;
      })
      .addMatcher(isPending(getEntities, getEntity, searchEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(uploadDocument, downloadDocument), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ErpaxaSlice.actions;

// Reducer
export default ErpaxaSlice.reducer;
